import React, {Suspense, useState} from 'react'
import PageLayout from '@view/layout/pageLayout/PageLayout'
import {Box, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material'
import RouterIcon from '@mui/icons-material/Router'
import {useTranslation} from 'react-i18next'
import SpinnerCustom from '@ui/spinner/SpinnerCustom'
import ConnectedMikrotik from '@containers/equipment/mikrotik/ConnectedMikrotik'
import {MikrotikTableLazy} from '@containers/equipment/mikrotik/mikrotikTable/MikrotikTable.lazy'
import {MikrotikInstructionLazy} from '@containers/equipment/mikrotik/instruction/MikrotikInstruction.lazy'
import {RouterSetupLazy} from '@containers/equipment/mikrotik/routerSetup/RouterSetup.lazy'
import {useAppContext} from '@context/appContext/hooks/useAppContext'
import AccountAccentScope from '@containers/accountStyle/scope/AccountAccentScope'

type EquipmentType = 'mikrotik'

type MikrotikTab = 'connected' | 'registry' | 'instruction' | 'setup'

const EquipmentPage = () => {
	const {t} = useTranslation()
	const {user} = useAppContext()
	const [type, setType] = useState<EquipmentType>('mikrotik')
	const [tab, setTab] = useState<MikrotikTab>('registry')

	/** Connected tab lists not-yet-configured MikroTiks of all clients; regular users mistake them for their own. */
	const canSeeConnected = Boolean(user?.superTenantAccount || user?.tenantAccount)

	const handleChangeType = (_: React.MouseEvent<HTMLElement>, value: EquipmentType | null) => {
		if (value) {
			setType(value)
		}
	}

	return (
		<PageLayout>
			<Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
				<Stack alignItems="center" flexShrink={0}>
					<Typography variant="h6" fontWeight={700}>
						{t('base.equipmentNav')}
					</Typography>

					<ToggleButtonGroup
						exclusive
						size="small"
						value={type}
						onChange={handleChangeType}
						sx={{mt: 1}}
					>
						<ToggleButton value="mikrotik">
							<RouterIcon fontSize="small" sx={{mr: 0.75}}/>
							MikroTik
						</ToggleButton>
					</ToggleButtonGroup>

					<AccountAccentScope>
						<Tabs
							value={tab}
							onChange={(_, value: MikrotikTab) => setTab(value)}
							sx={{mt: 1, mb: 2}}
						>
							<Tab value="registry" label={t('equipment.registry')}/>
							{canSeeConnected && <Tab value="connected" label={t('equipment.connected')}/>}
							<Tab value="instruction" label={t('equipment.instruction')}/>
							<Tab value="setup" label={t('equipment.routerSetup')}/>
						</Tabs>
					</AccountAccentScope>
				</Stack>

				<Box sx={{flex: 1, overflow: 'auto'}}>
					{tab === 'connected' && canSeeConnected && <ConnectedMikrotik/>}

					{tab === 'registry' && (
						<Suspense fallback={<SpinnerCustom/>}>
							<MikrotikTableLazy/>
						</Suspense>
					)}

					{tab === 'instruction' && (
						<Suspense fallback={<SpinnerCustom/>}>
							<MikrotikInstructionLazy/>
						</Suspense>
					)}

					{tab === 'setup' && (
						<Suspense fallback={<SpinnerCustom/>}>
							<RouterSetupLazy/>
						</Suspense>
					)}
				</Box>
			</Box>
		</PageLayout>
	)
}

export default EquipmentPage
